import { CommonDialogs } from "@mcleod/common/src/CommonDialogs";
import { DataSource, DropdownItem, Snackbar } from "@mcleod/components";
import { Api, DateUtil, FileUtil, NumberUtil, StringUtil } from "@mcleod/core";
import { PortalCompanySettings } from "@mcleod/portal-common";

export class LtlUtil {
    public static getTotalFormattedWeight(dataSource: DataSource): string {
        return NumberUtil.formatDecimal(this.getTotalWeight(dataSource) ?? 0, "#,###.#") + " lbs"
    }

    public static getTotalWeight(dataSource: DataSource): number {
        return this.getFgiTotal(dataSource, "weight");
    }

    public static formatTotalWeight(weight: number): string {
        return NumberUtil.formatDecimal(weight ?? 0, "#,###.#") + " lbs"
    }

    public static getFgiTotal(dataSource: DataSource, field: string): number {
        const data = dataSource?.data;
        if (data != null && data.length > 0)
            return data.map(row => parseFloat(row.get(field, 0).toString().replace(/,/g, ''))).reduce((prev, next) => prev + next);
        return 0;
    }

    public static getOverallClassCode(dataSource: DataSource): string {
        let classCode = null;
        for (const row of dataSource?.data) {
            const rowClass = row.get("nmfc_class_code");
            if (classCode == null) {
                classCode = rowClass;
            } else if (rowClass != classCode) {
                classCode = "Multiple";
                break;
            }
        }
        return classCode;
    }

    public static getPickupDateItems(date: any): DropdownItem[] {
        const stopDate = DateUtil.parseDateTime(date);
        const timeItems: DropdownItem[] = [];
        const companySettings = PortalCompanySettings.get();
        let start = new Date(companySettings.earliest_pickup);
        let end = new Date(companySettings.ord_latest_pickup_time);
        if (isNaN(start.getTime())) {
            start = new Date(stopDate);
            start.setHours(8, 0, 0, 0);
        }
        else {
            const temp = start;
            start = new Date(stopDate);
            start.setHours(temp.getHours(), temp.getMinutes(), 0, 0);
        }
        if (isNaN(end.getTime())) {
            end = new Date(stopDate);
            end.setHours(17, 0, 0, 0);
        }
        else {
            const temp = end;
            end = new Date(stopDate);
            end.setHours(temp.getHours(), temp.getMinutes(), 0, 0);
        }
        for (let d = start; d <= end; d.setMinutes(d.getMinutes() + 30)) {
            const value = DateUtil.formatDateTime(d, "HH:mm");
            timeItems.push(new DropdownItem(d.toString(), value));
        }
        return timeItems;
    }

    public static async downloadBOL(orderId: string) {
        if (StringUtil.isEmptyString(orderId)) {
            Snackbar.showWarningSnackbar("Unable to generate Bill of Lading; no order was specified", { persist: true });
            return;
        }
        const todaysDate = DateUtil.formatDate(new Date(), "MM/dd/yyyy");
        const fileName = `Order ${orderId}-BOL-${todaysDate}.pdf`;
        const message = "We are generating your Bill of Lading and it will download automatically.";
        const progressSnack: Snackbar = Snackbar.showDownloadSnackbar("Bill of Lading", message, { persist: true });
        try {
            const response = await Api.post("portal/reports/bill-of-lading", { order_id: orderId });
            const report = response?.data[0]?.bol_report;
            if (report == null) {
                progressSnack.dismiss();
                Snackbar.showWarningSnackbar("Unable to generate Bill of Lading for order " + orderId, { persist: true });
                return;
            }
            FileUtil.downloadBase64AsFile(report, fileName);
            Snackbar.showSnackbar("Downloaded " + fileName);
            progressSnack.dismiss();
        } catch (err) {
            progressSnack.dismiss();
            CommonDialogs.showError(err);
        }
    }

    public static async downloadApiShippingLabel(orderId: string) {
        if (StringUtil.isEmptyString(orderId)) {
            Snackbar.showWarningSnackbar("Unable to generate Shipping Label; no order was specified", { persist: true });
            return;
        }
        const todaysDate = DateUtil.formatDate(new Date(), "MM/dd/yyyy");
        const fileName = `Order ${orderId}-Shipping-Label-${todaysDate}.pdf`;
        const message = "We are generating your Shipping Label and it will download automatically.";
        const progressSnack: Snackbar = Snackbar.showDownloadSnackbar("Shipping Label", message, { persist: true });
        try {
            const response = await Api.search("portal/reports/download-api-document", { order_id: orderId});
            const data = response?.data[0];
            const shippingLabelDocument = data?.shipping_label_report;
            const success: boolean = data?.success;
            const errorMessage: string = data?.error_message ?? "Unable to generate Shipping Label for order " + orderId;
            if (success === false) {
                progressSnack.dismiss();
                Snackbar.showWarningSnackbar(errorMessage, { persist: true });
                return;
            }
            FileUtil.downloadBase64AsFile(shippingLabelDocument, fileName);
            Snackbar.showSnackbar("Downloaded " + fileName);
            progressSnack.dismiss();
        } catch (err) {
            progressSnack.dismiss();
            CommonDialogs.showError(err);
        }
    }

    public static async downloadApiBolDocument(orderId: string) {
        if (StringUtil.isEmptyString(orderId)) {
            Snackbar.showWarningSnackbar("Unable to generate BOL Document; no order was specified", { persist: true });
            return;
        }
        const todaysDate = DateUtil.formatDate(new Date(), "MM/dd/yyyy");
        const fileName = `Order ${orderId}-BOL-Document-${todaysDate}.pdf`;
        const message = "We are generating your BOL Document and it will download automatically.";
        const progressSnack: Snackbar = Snackbar.showDownloadSnackbar("BOL Document", message, { persist: true });
        try {
            const response = await Api.search("portal/reports/download-api-document", { order_id: orderId});
            const data = response?.data[0];
            const bolDocument = data?.bol_report;
            const success: boolean = data?.success;
            const errorMessage: string = data?.error_message ?? "Unable to generate BOL Document for order " + orderId;
            if (success === false) {
                progressSnack.dismiss();
                Snackbar.showWarningSnackbar(errorMessage, { persist: true });
                return;
            }
            FileUtil.downloadBase64AsFile(bolDocument, fileName);
            Snackbar.showSnackbar("Downloaded " + fileName);
            progressSnack.dismiss();
        } catch (err) {
            progressSnack.dismiss();
            CommonDialogs.showError(err);
        }
    }

    /** This is an event handler for the onClick event of buttonQuoteSheet.  */
    public static downloadQuoteSheet(quoteId: string) {
        if (quoteId != null) {
            const todaysDate = DateUtil.formatDate(new Date(), "MM/dd/yyyy");
            const fileName = `Quote ${quoteId}-Quote-Sheet-${todaysDate}.pdf`;
            const progressSnack: Snackbar = Snackbar.showDownloadSnackbar("Quote Sheet", "We are generating your Quote Sheet and it will download automatically.", { persist: true });
            Api.post("portal/reports/quote-sheet", { quote_id: quoteId }).then((response) => {
                const report = response?.data[0]?.quote_sheet_report;
                if (report != null) {
                    FileUtil.downloadBase64AsFile(report, fileName);
                    Snackbar.showSnackbar("Downloaded " + fileName);
                    progressSnack.dismiss();
                }
            }).catch((err) => {
                progressSnack.dismiss();
                Snackbar.showWarningSnackbar(err, { persist: true });
            })
        }
    }

}
